import {
  type NotificationPreferencesData,
  type NotificationScope,
  defaultNotificationPreferences,
  notificationCategoryLabels,
  notificationScopeLabels,
  notificationScopeValues,
} from '@borradh-workspace/api-client/types';
import { createFileRoute } from '@tanstack/react-router';
import { useId } from 'react';

import { PageShell } from '@/components/app/page-shell';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { useWhatsappLinkStatus } from '@/features/assistant/api/use-whatsapp-link-status';
import { useGetNotificationPreferences } from '@/features/notifications';
import { useUpdateNotificationPreferences } from '@/features/notifications';

export const Route = createFileRoute(
  '/_authed/dashboard/settings/notifications'
)({
  component: NotificationsSettingsPage,
});

// --- Channel toggle row (Email / Push) ---

interface ChannelToggleProps {
  label: string;
  checked: boolean;
  disabled?: boolean;
  onCheckedChange: (value: boolean) => void;
}

function ChannelToggle({
  label,
  checked,
  disabled,
  onCheckedChange,
}: ChannelToggleProps) {
  const id = useId();
  return (
    <div className="flex items-center justify-between gap-3">
      <Label htmlFor={id} className="text-muted-foreground text-xs font-normal">
        {label}
      </Label>
      <Switch
        id={id}
        data-testid={`channel-toggle-${label.toLowerCase()}`}
        checked={checked}
        disabled={disabled}
        onCheckedChange={onCheckedChange}
      />
    </div>
  );
}

// --- Scoped category row (Appointments, Inbox handoffs, New leads) ---

interface ScopedCategoryRowProps {
  category: 'appointments' | 'inbox' | 'leads';
  scope: NotificationScope;
  email: boolean;
  push: boolean;
  disabled: boolean;
  separator: boolean;
  /** Hidden for categories that deliberately have no email channel. */
  showEmail?: boolean;
  /**
   * Shown only for categories that have an approved WhatsApp template behind
   * them (inbox handoffs today). A toggle for a category with no template
   * would be a switch that silently sends nothing.
   */
  showWhatsapp?: boolean;
  whatsapp?: boolean;
  /** Whether this person has paired a number under Settings → Claire on WhatsApp. */
  whatsappPaired?: boolean;
  onScopeChange: (scope: NotificationScope) => void;
  onEmailChange: (value: boolean) => void;
  onPushChange: (value: boolean) => void;
  onWhatsappChange?: (value: boolean) => void;
}

function ScopedCategoryRow({
  category,
  scope,
  email,
  push,
  disabled,
  separator,
  showEmail = true,
  showWhatsapp = false,
  whatsapp = false,
  whatsappPaired = false,
  onScopeChange,
  onEmailChange,
  onPushChange,
  onWhatsappChange,
}: ScopedCategoryRowProps) {
  const scopeId = useId();
  return (
    <div className={separator ? 'border-border border-b py-4' : 'py-4'}>
      <div className="flex items-center justify-between gap-4">
        <Label htmlFor={scopeId} className="font-medium text-sm">
          {notificationCategoryLabels[category]}
        </Label>
        <Select
          value={scope}
          disabled={disabled}
          onValueChange={(value) => onScopeChange(value as NotificationScope)}
        >
          <SelectTrigger id={scopeId} size="sm" className="w-[130px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {notificationScopeValues.map((value) => (
              <SelectItem key={value} value={value}>
                {notificationScopeLabels[value]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="mt-3 space-y-2">
        {showEmail && (
          <ChannelToggle
            label="Email"
            checked={email}
            disabled={disabled}
            onCheckedChange={onEmailChange}
          />
        )}
        <ChannelToggle
          label="Push"
          checked={push}
          disabled={disabled}
          onCheckedChange={onPushChange}
        />
        {showWhatsapp && onWhatsappChange && (
          <div>
            <ChannelToggle
              label="WhatsApp"
              checked={whatsapp}
              // Nothing to send to until a number is paired, so the control
              // says why instead of accepting a setting that cannot act.
              disabled={disabled || !whatsappPaired}
              onCheckedChange={onWhatsappChange}
            />
            {!whatsappPaired && (
              <p className="mt-1 text-muted-foreground text-xs">
                Pair your number under Settings → Claire on WhatsApp to use
                this.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// --- Toggle category row (org-wide on/off categories) ---

interface ToggleCategoryRowProps {
  category: 'advertising' | 'integrations';
  enabled: boolean;
  email: boolean;
  push: boolean;
  disabled: boolean;
  separator: boolean;
  onEnabledChange: (value: boolean) => void;
  onEmailChange: (value: boolean) => void;
  onPushChange: (value: boolean) => void;
}

function ToggleCategoryRow({
  category,
  enabled,
  email,
  push,
  disabled,
  separator,
  onEnabledChange,
  onEmailChange,
  onPushChange,
}: ToggleCategoryRowProps) {
  const enabledId = useId();
  return (
    <div className={separator ? 'border-border border-b py-4' : 'py-4'}>
      <div className="flex items-center justify-between gap-4">
        <Label htmlFor={enabledId} className="font-medium text-sm">
          {notificationCategoryLabels[category]}
        </Label>
        <Switch
          id={enabledId}
          checked={enabled}
          disabled={disabled}
          onCheckedChange={onEnabledChange}
        />
      </div>
      <div className="mt-3 space-y-2">
        <ChannelToggle
          label="Email"
          checked={email}
          disabled={disabled || !enabled}
          onCheckedChange={onEmailChange}
        />
        <ChannelToggle
          label="Push"
          checked={push}
          disabled={disabled || !enabled}
          onCheckedChange={onPushChange}
        />
      </div>
    </div>
  );
}

// --- Always-on category row ---

interface CriticalCategoryRowProps {
  category: 'integrations';
  push: boolean;
  disabled: boolean;
  onPushChange: (value: boolean) => void;
}

/**
 * A category that cannot be switched off.
 *
 * `integration_disconnected` is delivered in-app and by email whatever the
 * preferences say — a muted "your Instagram stopped replying" is another silent
 * blackout, which is the exact failure the alert exists to end. So this row
 * states that plainly rather than rendering dead switches, and offers the one
 * choice that IS honoured: push.
 */
function CriticalCategoryRow({
  category,
  push,
  disabled,
  onPushChange,
}: CriticalCategoryRowProps) {
  return (
    <div className="py-4">
      <div className="flex items-center justify-between gap-4">
        <div className="flex-1">
          <p className="font-medium text-sm">
            {notificationCategoryLabels[category]}
          </p>
          <p className="mt-0.5 text-muted-foreground text-xs">
            Always sent by email so a broken connection can't go unnoticed.
          </p>
        </div>
      </div>
      <div className="mt-3 space-y-2">
        <ChannelToggle
          label="Push"
          checked={push}
          disabled={disabled}
          onCheckedChange={onPushChange}
        />
      </div>
    </div>
  );
}

// --- Legacy marketing switch row ---

interface MarketingRowProps {
  title: string;
  description: string;
  checked: boolean;
  disabled: boolean;
  separator: boolean;
  onCheckedChange: (value: boolean) => void;
}

function MarketingRow({
  title,
  description,
  checked,
  disabled,
  separator,
  onCheckedChange,
}: MarketingRowProps) {
  const id = useId();
  return (
    <div
      className={
        separator
          ? 'flex items-center justify-between gap-4 border-border border-b py-4'
          : 'flex items-center justify-between gap-4 py-4'
      }
    >
      <div className="flex-1">
        <Label htmlFor={id} className="font-medium text-sm">
          {title}
        </Label>
        <p className="mt-0.5 text-muted-foreground text-xs">{description}</p>
      </div>
      <Switch
        id={id}
        checked={checked}
        disabled={disabled}
        onCheckedChange={onCheckedChange}
      />
    </div>
  );
}

function NotificationsSettingsPage() {
  const { preferences, isLoading } = useGetNotificationPreferences();
  const { update, isUpdating } = useUpdateNotificationPreferences();
  // Whether this person has a verified number to receive on. A revoked or
  // pending link is not somewhere we can send.
  //
  // ABOVE the early returns below, and it must stay there: the loading and
  // unavailable branches return before this point, so a hook placed after them
  // runs on some renders and not others. React then throws "rendered more
  // hooks than during the previous render" the moment preferences arrive, and
  // the whole page goes blank rather than merely losing this one control.
  const { link: whatsappLink } = useWhatsappLinkStatus();
  const whatsappPaired = whatsappLink?.status === 'active';

  if (isLoading && !preferences) {
    return (
      <PageShell className="pt-10 pb-8" maxWidth="max-w-[960px]">
        <title>Notifications | Borrah</title>
        <Skeleton className="h-72 w-full rounded-xl" />
        <Skeleton className="h-56 w-full rounded-xl" />
      </PageShell>
    );
  }

  if (!preferences) {
    return (
      <PageShell className="pt-10 pb-8" maxWidth="max-w-[960px]">
        <title>Notifications | Borrah</title>
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground text-sm">
            Notification preferences are unavailable right now. Please try again
            later.
          </CardContent>
        </Card>
      </PageShell>
    );
  }

  const disabled = isUpdating;
  // Granular per-category settings live in the `preferences` JSON column.
  // Start from a complete object so updates never send a partial shape.
  const prefs: NotificationPreferencesData =
    preferences.preferences ?? defaultNotificationPreferences;

  // Each handler builds the COMPLETE updated preferences object (one field
  // changed) — the API expects the full shape.
  const setScope = (
    category: 'appointments' | 'inbox' | 'leads',
    scope: NotificationScope
  ) => {
    update({
      preferences: {
        ...prefs,
        [category]: { ...prefs[category], scope },
      },
    });
  };

  const setScopedChannel = (
    category: 'appointments' | 'inbox' | 'leads',
    channel: 'email' | 'push' | 'whatsapp',
    value: boolean
  ) => {
    update({
      preferences: {
        ...prefs,
        [category]: {
          ...prefs[category],
          channels: { ...prefs[category].channels, [channel]: value },
        },
      },
    });
  };

  const setAdvertisingEnabled = (enabled: boolean) => {
    update({
      preferences: {
        ...prefs,
        advertising: { ...prefs.advertising, enabled },
      },
    });
  };

  // Only push is settable: in-app and email delivery for a broken connection
  // is account-critical and ignores preferences server-side, so offering
  // switches for them would be a lie.
  const setIntegrationsPush = (value: boolean) => {
    update({
      preferences: {
        ...prefs,
        integrations: {
          ...prefs.integrations,
          channels: { ...prefs.integrations.channels, push: value },
        },
      },
    });
  };

  const setAdvertisingChannel = (channel: 'email' | 'push', value: boolean) => {
    update({
      preferences: {
        ...prefs,
        advertising: {
          ...prefs.advertising,
          channels: { ...prefs.advertising.channels, [channel]: value },
        },
      },
    });
  };

  return (
    <PageShell className="pt-10 pb-8" maxWidth="max-w-[960px]">
      <title>Notifications | Borrah</title>

      <Card className="gap-0 py-0">
        <CardHeader className="px-6 pt-6 pb-2">
          <CardTitle>Notifications</CardTitle>
          <CardDescription>
            Choose which events reach you and how they're delivered.
          </CardDescription>
        </CardHeader>
        <CardContent className="px-6 pt-2 pb-2">
          <ScopedCategoryRow
            category="appointments"
            scope={prefs.appointments.scope}
            email={prefs.appointments.channels.email}
            push={prefs.appointments.channels.push}
            disabled={disabled}
            separator
            onScopeChange={(scope) => setScope('appointments', scope)}
            onEmailChange={(v) => setScopedChannel('appointments', 'email', v)}
            onPushChange={(v) => setScopedChannel('appointments', 'push', v)}
          />
          <ScopedCategoryRow
            category="inbox"
            scope={prefs.inbox.scope}
            email={prefs.inbox.channels.email}
            push={prefs.inbox.channels.push}
            disabled={disabled}
            separator
            showWhatsapp
            whatsapp={prefs.inbox.channels.whatsapp}
            whatsappPaired={whatsappPaired}
            onScopeChange={(scope) => setScope('inbox', scope)}
            onEmailChange={(v) => setScopedChannel('inbox', 'email', v)}
            onPushChange={(v) => setScopedChannel('inbox', 'push', v)}
            onWhatsappChange={(v) => setScopedChannel('inbox', 'whatsapp', v)}
          />
          <ScopedCategoryRow
            category="leads"
            scope={prefs.leads.scope}
            email={prefs.leads.channels.email}
            push={prefs.leads.channels.push}
            disabled={disabled}
            separator
            showEmail={false}
            onScopeChange={(scope) => setScope('leads', scope)}
            onEmailChange={(v) => setScopedChannel('leads', 'email', v)}
            onPushChange={(v) => setScopedChannel('leads', 'push', v)}
          />
          <ToggleCategoryRow
            category="advertising"
            enabled={prefs.advertising.enabled}
            email={prefs.advertising.channels.email}
            push={prefs.advertising.channels.push}
            disabled={disabled}
            separator
            onEnabledChange={setAdvertisingEnabled}
            onEmailChange={(v) => setAdvertisingChannel('email', v)}
            onPushChange={(v) => setAdvertisingChannel('push', v)}
          />
          <CriticalCategoryRow
            category="integrations"
            push={prefs.integrations.channels.push}
            disabled={disabled}
            onPushChange={(v) => setIntegrationsPush(v)}
          />
        </CardContent>
      </Card>

      <Card className="gap-0 py-0">
        <CardHeader className="px-6 pt-6 pb-2">
          <CardTitle>Marketing &amp; updates</CardTitle>
          <CardDescription>
            Product news and summaries — not tied to your account activity.
          </CardDescription>
        </CardHeader>
        <CardContent className="px-6 pt-2 pb-2">
          <MarketingRow
            title="Marketing emails"
            description="Product tips, case studies, and offers."
            checked={preferences.marketingEmails}
            disabled={disabled}
            separator
            onCheckedChange={(v) => update({ marketingEmails: v })}
          />
          <MarketingRow
            title="App updates"
            description="Major releases and new feature announcements."
            checked={preferences.appUpdates}
            disabled={disabled}
            separator
            onCheckedChange={(v) => update({ appUpdates: v })}
          />
          <MarketingRow
            title="Weekly digest"
            description="A Monday summary of bookings, leads, and Claire activity."
            checked={preferences.weeklyDigest}
            disabled={disabled}
            separator={false}
            onCheckedChange={(v) => update({ weeklyDigest: v })}
          />
        </CardContent>
      </Card>
    </PageShell>
  );
}
